/**
 * event-entities — fonte ÚNICA do mapa "tipo de evento → entidade".
 *
 * Antes de 2026-08-30 esta cascata estava copiada à mão em quatro lugares:
 * o cálculo de campos observáveis e o de filtros no `EventTriggerConfig`, o
 * `providesContext` em `node-schemas.deriveEventTriggerSchema` e a derivação
 * do modal de teste. As quatro cópias pararam em `contact.` e esqueceram o
 * evento de agenda — e o sintoma apareceu em três telas diferentes de uma vez
 * (aba Filtros vazia, "Observar campos específicos" sumido, `{{event.*}}`
 * fora do autocomplete).
 *
 * Quem adicionar família de evento nova mexe AQUI, e as quatro telas andam
 * juntas.
 */
import type { ContextCategory } from '../types/variables';

/**
 * Entidade principal que o gatilho entrega ao fluxo — a mesma que o
 * `EventTriggerDispatcher` deriva no backend para o Entity Enrichment.
 * `null` = o gatilho não injeta entidade nenhuma.
 *
 * ⚠️ `calendar_event.cancelled` é `null` de propósito, e não por esquecimento:
 * o `CalendarService` faz o soft delete do evento ANTES de publicar o evento
 * de domínio, e o carregamento do enrichment filtra `deletedAt`. Ou seja,
 * `inputData.event` nunca é populado no cancelamento — oferecer os campos
 * dele na tela seria oferecer um filtro que jamais é aplicado. Os dados do
 * evento cancelado seguem acessíveis por `{{triggerData.*}}`, que o publisher
 * preenche (eventId, summary, startTime, endTime, contactId, userId, status).
 */
export function entityForEventType(eventType: string): ContextCategory | null {
  if (eventType.startsWith('lead.')) return 'lead';
  if (eventType.startsWith('ticket.')) return 'ticket';
  if (eventType.startsWith('contact.')) return 'contact';
  if (eventType.startsWith('conversation.') || eventType.startsWith('message.')) return 'conversation';
  if (eventType === 'calendar_event.created' || eventType === 'calendar_event.updated') return 'event';
  // database.*, form.*, custom.* e calendar_event.cancelled: sem entidade.
  return null;
}

/**
 * Entidades cujos campos a aba "Filtros" do gatilho oferece.
 *
 * Difere de `entityForEventType` porque eventos que carregam um contato junto
 * (lead, ticket, conversa) também deixam filtrar POR ele — o backend carrega
 * o contato no mesmo enrichment.
 *
 * Evento de agenda fica só com `event`: o contato só existe quando o
 * compromisso foi marcado com alguém, e prometer campos de contato num evento
 * que pode não ter nenhum daria filtro que nunca casa.
 */
export function filterableEntitiesForEventType(eventType: string): ContextCategory[] {
  const entidade = entityForEventType(eventType);
  if (entidade === null) return [];
  if (entidade === 'contact' || entidade === 'event') return [entidade];
  return [entidade, 'contact'];
}

/**
 * Entidades que a aba "Filtros" do `NodeConfigPanel` oferece para um gatilho,
 * por `nodeType` — usado pelo painel compartilhado (Task 13, spec 2026-09-14)
 * para não repetir a lógica de cada um dos 3 componentes de gatilho.
 *
 * `trigger_date_field`/`trigger_inactivity` sempre entregam a própria
 * entidade (nunca vazio); só `trigger_event` pode devolver `[]` (evento sem
 * entidade — ver `entityForEventType`).
 */
export function filterEntitiesForTrigger(nodeType: string, config: Record<string, unknown>): ContextCategory[] {
  if (nodeType === 'trigger_event') return filterableEntitiesForEventType(String(config.eventType ?? ''));
  if (nodeType === 'trigger_date_field') return [String(config.entityType ?? 'contact') as ContextCategory];
  if (nodeType === 'trigger_inactivity') return [String(config.entityType ?? 'conversation') as ContextCategory];
  return [];
}
